import type { ChangeEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, type Team } from "@/lib/api";

interface UseResourceActionsOptions {
	projectId: string | undefined;
	userId: string | undefined;
	activeTeam: Team | null;
}

/** The Resources panel's actions: uploading files and generating a presentation. */
export function useResourceActions({
	projectId,
	userId,
	activeTeam,
}: UseResourceActionsOptions) {
	const { i18n } = useTranslation();
	const queryClient = useQueryClient();

	const uploadMutation = useMutation({
		mutationFn: async (selectedFiles: File[]) => {
			if (!userId || !projectId) {
				throw new Error("Missing user or project context for upload");
			}

			// One at a time: the server takes two uploads per person at once and
			// refuses the rest.
			const uploaded = [];
			for (const file of selectedFiles) {
				uploaded.push(
					await api.uploadFile({
						file,
						userId,
						conversationId: projectId,
						...(activeTeam ? { teamId: activeTeam.id } : {}),
					}),
				);
			}
			return uploaded;
		},
		onSuccess: (_, selectedFiles) => {
			queryClient.invalidateQueries({ queryKey: ["projectFiles", projectId] });
			const count = selectedFiles.length;
			toast.success(
				`${count} file${count > 1 ? "s" : ""} uploaded successfully`,
			);
		},
		onError: (error) => {
			toast.error(
				error instanceof Error ? error.message : "Failed to upload files",
			);
		},
	});

	const presentationMutation = useMutation({
		mutationFn: () => {
			if (!projectId) throw new Error("No project");
			const lang = (i18n.resolvedLanguage ?? i18n.language ?? "ru") as
				| "en"
				| "ru";
			return api.generatePresentation(projectId, lang);
		},
		onSuccess: () => {
			toast.success("Presentation generation started");
		},
		onError: (error) => {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to start presentation generation",
			);
		},
	});

	const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(e.target.files || []);
		if (selectedFiles.length > 0) {
			uploadMutation.mutate(selectedFiles);
		}
		e.target.value = "";
	};

	return { uploadMutation, presentationMutation, handleFileSelect };
}
